import { Stack } from "@certn/cascadia/foundations/grid-layout/Stack";
import { Text } from "@certn/cascadia/foundations/typography/Text";
import { css } from "@emotion/css";

import { BasicButton } from "../../button/BasicButton";
import { Icon } from "../../../foundations/icons/Icon/Icon";
import { TextArea, TextAreaProps } from "../text-area/TextArea";
import { FormLabelRAC } from "../_assets/input-wrapper/FormLabelRAC";
import type { CascadiaCustomIcon } from "@certn/cascadia-tokens";

export interface AIAssistAction {
  id: string;
  label: string;
  icon?: CascadiaCustomIcon;
  onPress: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

export interface AIAssistTextAreaProps
  extends Omit<
    TextAreaProps,
    "value" | "onChange" | "isDisabled" | "placeholder"
  > {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  isRequired?: boolean;
  actions: AIAssistAction[];
  isBusy?: boolean;
  busyPlaceholder?: string;
  reviewNote?: string | null;
  stripTitle?: string;
  placeholder?: string;
}

export function AIAssistTextArea({
  value,
  onChange,
  label,
  isRequired,
  actions,
  isBusy = false,
  busyPlaceholder,
  reviewNote,
  stripTitle = "Generate with AI",
  placeholder,
  testID,
  errors,
  isInvalid,
  ...textAreaProps
}: AIAssistTextAreaProps) {
  const displayValue = isBusy ? "" : value;
  const fieldPlaceholder = isBusy ? busyPlaceholder : placeholder;

  return (
    <Stack gap="space-1" data-testid={testID}>
      {label && <FormLabelRAC isRequired={isRequired}>{label}</FormLabelRAC>}
      <div>
        <Stack
          orientation="horizontal"
          className={styles.heading}
          justify="space-between"
          align="center"
        >
          <Stack orientation="horizontal" gap="space-1" align="center">
            <Icon name="ai_spark" type="custom" size="space-5" />
            <Text style="global/label-small" color="text-base">
              {stripTitle}
            </Text>
          </Stack>
          <Stack orientation="horizontal" gap="space-1" align="center">
            {actions.map((action) => (
              <BasicButton
                key={action.id}
                className={styles.actionButton}
                leftIcon={action.icon ?? "ai_spark"}
                onPress={action.onPress}
                size="small"
                isDisabled={
                  isBusy ||
                  action.isDisabled ||
                  (!value.trim() && action.id === "improve")
                }
                isLoading={action.isLoading}
                aria-label={action.label}
              >
                {action.label}
              </BasicButton>
            ))}
          </Stack>
        </Stack>
        <div className={styles.messageContainer}>
          <TextArea
            className={styles.messageInput}
            isInvalid={isInvalid}
            errors={errors}
            value={displayValue}
            onChange={onChange}
            testID={testID ? `${testID}-input` : "ai-assist-text-area-input"}
            resize="none"
            placeholder={fieldPlaceholder}
            isDisabled={isBusy}
            aria-label={label}
            isRequired={isRequired}
            {...textAreaProps}
          />
        </div>
      </div>
      {reviewNote && (
        <Text
          style="input/helper"
          color="text-weak"
          testID={`${testID}-review-note`}
        >
          {reviewNote}
        </Text>
      )}
    </Stack>
  );
}

const styles = {
  heading: css({
    borderTopRightRadius: "var(--border-radius-2)",
    borderTopLeftRadius: "var(--border-radius-2)",
    border: "1px solid var(--border-success-hover)",
    padding: "var(--space-2)",
    minHeight: "48px",
    background:
      "linear-gradient(90deg, rgba(27, 183, 147, 0.05) 0%, rgba(12, 81, 65, 0.05) 100%)",
  }),
  actionButton: css({
    "&:not(:focus-within)": {
      boxShadow: "inset 0 0 0 1px var(--border-success) !important",
    },
  }),
  messageContainer: css({
    "& > div": {
      borderRadius: 0,
      borderBottomRightRadius: "var(--border-radius-2)",
      borderBottomLeftRadius: "var(--border-radius-2)",
      borderColor: "var(--border-input)",
      boxShadow: "none",
      borderWidth: "1px",
      borderStyle: "solid",
      borderTop: "none",
    },
    "& textarea": {
      height: "88px",
      boxShadow: "none",
      border: "none",
    },
  }),
  messageInput: css({
    border: "none",
  }),
};
